import { ref } from "vue";
import { logger } from "@/infrastructure/services/LoggerService";
import { useDirtyState } from "./forms/useDirtyState";
/**
 * Generic composable for entity-layout load, save, snapshot, and dirty tracking.
 * Owns isLoading / isSaving / error / isDirty so any entity (org, contact, donation, …)
 * can reuse the same plumbing without duplicating it in a domain-specific store.
 *
 * When dirtyData is provided this composable must be called from component setup context
 * because useDirtyState registers an onBeforeRouteLeave guard.
 *
 * Usage (with dirty tracking):
 *   const { isDirty, isSaving, save, ... } = useEntitySave(
 *     fetchLayout, mapLayout, "myDomain.loadError", state, { onNavigationBlocked })
 *
 * Usage (load-only, e.g. in a Pinia store):
 *   const { loadEntity, currentEntity, ... } = useEntitySave(fetchLayout, mapLayout, "key")
 */
export function useEntitySave(fetchLayout, mapLayout, loadErrorKey, dirtyData, dirtyOptions) {
    const entityLayout = ref(null);
    const originalGenericValues = ref({});
    const currentEntity = ref(null);
    const isLoading = ref(false);
    const isSaving = ref(false);
    const error = ref(null);
    const dirtyApi = dirtyData !== undefined
        ? useDirtyState({
            data: dirtyData,
            onNavigationBlocked: dirtyOptions?.onNavigationBlocked,
            enableBeforeUnload: dirtyOptions?.enableBeforeUnload ?? true,
        })
        : null;
    const isDirty = dirtyApi?.isDirty ?? ref(false);
    const hasPendingNavigation = dirtyApi?.hasPendingNavigation ?? ref(false);
    const markAsSaved = dirtyApi?.markAsSaved ??
        (() => {
            /* no-op */
        });
    const resetToOriginal = dirtyApi?.resetToOriginal ??
        (() => {
            /* no-op */
        });
    const setDirty = dirtyApi?.setDirty ??
        ((_dirty) => {
            /* no-op */
        });
    const proceedNavigation = dirtyApi?.proceedNavigation ??
        (() => {
            /* no-op */
        });
    const cancelNavigation = dirtyApi?.cancelNavigation ??
        (() => {
            /* no-op */
        });
    function snapshotGenericValues(layout) {
        return Object.fromEntries(layout.layout.blocks
            .filter((b) => b.kind === "generic")
            .map((b) => [b.id, Object.fromEntries(b.fields.map((f) => [f.key, f.value]))]));
    }
    function applyLayout(layout) {
        entityLayout.value = layout;
        originalGenericValues.value = snapshotGenericValues(layout);
        currentEntity.value = mapLayout(layout);
    }
    async function loadEntity(id) {
        if (!id)
            return;
        isLoading.value = true;
        error.value = null;
        try {
            applyLayout(await fetchLayout(id));
        }
        catch (err) {
            error.value = loadErrorKey;
            logger.error("[useEntitySave] loadEntity failed:", err);
        }
        finally {
            isLoading.value = false;
        }
    }
    async function save(id, performSave) {
        isSaving.value = true;
        error.value = null;
        try {
            const saveResponse = await performSave();
            // Thread `properties` (audit metadata) through alongside the refreshed layout so
            // consumers (e.g. the "Properties" popup) reflect post-save createdAt/updatedAt/
            // createdBy/updatedBy without a full page refresh. Dropping it here forced a stale
            // `—` until the next GET — see OP-6154.
            const layout = saveResponse.id && saveResponse.layout
                ? {
                    id: saveResponse.id,
                    layout: saveResponse.layout,
                    properties: saveResponse.properties,
                }
                : await fetchLayout(id);
            applyLayout(layout);
        }
        catch (err) {
            logger.error("[useEntitySave] save failed:", err);
            throw err;
        }
        finally {
            isSaving.value = false;
        }
    }
    function clearEntity() {
        entityLayout.value = null;
        originalGenericValues.value = {};
        currentEntity.value = null;
        error.value = null;
    }
    return {
        entityLayout,
        originalGenericValues,
        currentEntity,
        isLoading,
        isSaving,
        error,
        isDirty,
        loadEntity,
        save,
        clearEntity,
        markAsSaved,
        resetToOriginal,
        setDirty,
        proceedNavigation,
        cancelNavigation,
        hasPendingNavigation,
    };
}
